/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
import { useState } from 'react';

import { ZodType, z } from 'zod';

export enum StorageType {
  Local = 'localStorage',
  Session = 'sessionStorage',
}

const getStorage = (type: StorageType): Storage => {
  return type === StorageType.Session ? sessionStorage : localStorage;
};

/**
 *
 * @param item string that reperesent key in local storage you wanna get
 * @param schema optional parameter to parse value from local storage
 * @returns item from storage | null
 */
export const getItemFromStorage = <T extends unknown>(
  item: string,
  type: StorageType = StorageType.Local,
  schema: ZodType<T, any> = z.any()
): T | null => {
  try {
    const storage = getStorage(type);
    const itemFromStorage = storage.getItem(item);

    /**
     * if item is string representing null OR undefined -> return null
     */
    if (
      typeof itemFromStorage === 'string' &&
      (itemFromStorage === 'undefined' || itemFromStorage === 'null')
    )
      return null;

    /**
     * if item is simple string -> return the value as it is
     */
    // if (typeof itemFromStorage === "string") return itemFromStorage as T;

    return itemFromStorage ? schema.parse(JSON.parse(itemFromStorage)) : null;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const setItemInStorage = (
  item: string,
  value: unknown,
  type: StorageType = StorageType.Local
): boolean => {
  try {
    const storage = getStorage(type);
    storage.setItem(item, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const removeItemFromStorage = (item: string, type: StorageType = StorageType.Local) => {
  const storage = getStorage(type);
  storage.removeItem(item);
};

export const useStorage = <T>(
  key: string,
  initialValue: T,
  type: StorageType = StorageType.Local
): [T, (value: T | ((val: T) => T)) => void] => {
  const storage = getStorage(type);
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = storage.getItem(key);

      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);

      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      storage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
};

export const getItem = <T>(n: string, type: StorageType = StorageType.Local): T | null => {
  const storage = getStorage(type);
  const item = storage.getItem(n);
  if (!item) return null;
  return JSON.parse(item);
};

export const setItem = (n: string, v: unknown, type: StorageType = StorageType.Local) => {
  try {
    const storage = getStorage(type);
    storage.setItem(n, JSON.stringify(v));
    return true;
  } catch (e) {
    console.error('Your local storage is full. Please clear the data to continue view this page.');
    return false;
  }
};
